import React, { useEffect, useState } from "react";
import { primaryColor } from "../services/theme_service";
import closeIcon from "../assets/images/close.png";
import { Address } from "../models/address_model";
import { addAddress, updateAddress } from "../services/api_service";
import { ToastContainer, toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";

type AddAddressProps = {
  open: boolean;
  onClose?: () => void;
  address?: Address;
  onSave?: () => void;
};

type FormState = {
  name: string;
  mobile: string;
  pincode: string;
  locality: string;
  flat: string;
  landmark: string;
  city: string;
  state: string;
  primary: boolean;
  type?: string;
};

const AddAddress: React.FC<AddAddressProps> = ({ open, onClose, address, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(open);
  const [animate, setAnimate] = useState(false);
  const userId = localStorage.getItem("userId");
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [form, setForm] = useState<FormState>({
    name: "",
    mobile: "",
    pincode: "",
    locality: "",
    flat: "",
    landmark: "",
    city: "",
    state: "",
    primary: false,
    type: "",
  });

  useEffect(() => {
    if (open) {
      setShow(true);
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
      setTimeout(() => setShow(false), 300);
    }
  }, [open]);

  useEffect(() => {
    if (address) {
      setForm({
        name: address.first_name,
        mobile: address.phone_number,
        pincode: address.zip_code,
        locality: address.address2,
        flat: address.address1,
        landmark: address.land_mark,
        city: address.city,
        state: address.state,
        primary: address.is_default,
        type: "",
      });
    }
  }, [address]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (name === "mobile") {
      const numericValue = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, mobile: numericValue }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleReset = () => {
    setForm({
      name: "",
      mobile: "",
      pincode: "",
      locality: "",
      flat: "",
      landmark: "",
      city: "",
      state: "",
      primary: false,
      type: "",
    });
  };

  const validate = () => {
    const requiredFields: [keyof FormState, string][] = [
      ["name", "Name"],
      ["mobile", "Mobile Number"],
      ["pincode", "Pincode"],
      ["locality", "Locality / Area / Street"],
      ["flat", "Flat No / Building No"],
      ["city", "District / City"],
      ["state", "State"],
    ];

    for (let [key, label] of requiredFields) {
      if (!form[key]) {
        toast.error(`${label} is required`);
        return false;
      }
    }

    if (form.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);

    const payload: Address = {
      address_id: address?.address_id,
      first_name: form.name,
      last_name: "",
      address1: form.flat,
      address2: form.locality,
      land_mark: form.landmark,
      city: form.city,
      state: form.state,
      zip_code: form.pincode,
      country: "India",
      email: "",
      phone_number: form.mobile,
      longitude: "",
      latitude: "",
      is_default: form.primary,
    };

    try {
      if (userId) {
        const success = address
          ? await updateAddress(userId, payload)
          : await addAddress(userId, payload);

        if (success) {
          onSave?.();
          onClose?.();
        } else {
          toast.error("Failed to save address");
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Error saving address");
    }

    setLoading(false);
  };

  const fields: [keyof FormState, string][] = [
    ["name", "Name"],
    ["mobile", "Mobile Number"],
    ["pincode", "Pincode"],
    ["locality", "Locality / Area / Street"],
    ["flat", "Flat No / Building No"],
    ["landmark", "Landmark (optional)"],
    ["city", "District / City"],
    ["state", "State"],
  ];

  if (!show) return null;

  return (
    <>
      <ToastContainer />
      <div
        style={{ ...styles.overlay, opacity: animate ? 1 : 0, transition: "opacity 0.3s ease" }}
        onClick={() => !loading && onClose?.()}
      />
      <div
        style={{
          position: "fixed",
          background: "#fff",
          zIndex: 2001,
          padding: "20px",
          boxShadow: isMobile
            ? "0 -2px 10px rgba(0,0,0,0.2)"
            : "-2px 0 10px rgba(0,0,0,0.2)",
          boxSizing: "border-box",
          ...(isMobile
            ? {
                bottom: 0,
                left: 0,
                width: "100vw",
                height: "85dvh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                transform: animate ? "translateY(0)" : "translateY(100%)",
                transition: "transform 0.3s ease-in-out",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }
            : {
                top: 0,
                right: 0,
                width: "30%",
                height: "100dvh",
                transform: animate ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.3s ease-in-out",
                overflowY: "auto",
              }),
        }}
      >
        <div style={styles.header}>
          <img
            src={closeIcon}
            alt="close"
            style={styles.closeIcon}
            onClick={() => !loading && onClose?.()}
          />
          <span style={styles.title}>
            {address ? "Edit Address" : "Add New Address"}
          </span>
        </div>

        <div style={styles.form}>
          {fields.map(([key, placeholder]) => (
            <input
              key={key}
              type="text"
              name={key}
              value={form[key] as string}
              placeholder={placeholder}
              onChange={handleChange}
              style={styles.input}
            />
          ))}

          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              name="primary"
              checked={form.primary}
              onChange={handleChange}
              style={{ accentColor: primaryColor, height: 20, width: 20 }}
            />
            <span>Make this primary address</span>
          </div>

          <div style={styles.actions}>
            <button
              style={styles.resetBtn}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
            <button
              style={styles.saveBtn}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: "100vw",
    background: "rgba(0,0,0,0.3)",
    zIndex: 2000,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    marginTop: "10px",
    flexShrink: 0,
  },
  closeIcon: { width: 30, height: 30, marginRight: 15, cursor: "pointer" },
  title: { fontSize: 18, fontWeight: "bold" },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: 20,
    boxSizing: "border-box",
  },
  input: {
    padding: "10px",
    border: "1px solid #ccc",
    borderRadius: 8,
    fontSize: 16,
    outline: "none",
    width: "100%", // prevent overflow
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: 10,
    fontSize: 16,
    flexShrink: 0,
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 20,
    width: "100%",
    gap: 10,
    flexShrink: 0,
  },
  resetBtn: {
    flex: 1,
    padding: "10px",
    border: `1px solid ${primaryColor}`,
    borderRadius: 6,
    background: "transparent",
    color: primaryColor,
    cursor: "pointer",
    fontSize: 16,
    maxWidth: "100%",
  },
  saveBtn: {
    flex: 1,
    padding: "10px",
    border: "none",
    borderRadius: 6,
    background: primaryColor,
    color: "#fff",
    cursor: "pointer",
    fontSize: 16,
    maxWidth: "100%",
  },
};

export default AddAddress;
